import type { BadgeColor } from "@/shared/ui";
import type { StatusMapping } from "@/shared/components/StatusIndicator/mappings";
import type { TaskStatus, TaskPriority, TaskType, TagColor } from "./types";

interface StatusMapEntry {
  label: string;
  color: BadgeColor;
  icon?: React.ReactNode;
}

export const TASK_STATUS_MAP: Record<TaskStatus, StatusMapEntry> = {
  backlog: { label: "taskStatus.backlog", color: "gray" },
  todo: { label: "taskStatus.todo", color: "gray" },
  in_progress: { label: "taskStatus.inProgress", color: "notice" },
  blocked: { label: "taskStatus.blocked", color: "negative" },
  done: { label: "taskStatus.done", color: "positive" },
} as const;

export const TASK_STATUS_INDICATOR_MAP: Record<TaskStatus, StatusMapping> = {
  backlog: {
    bg: "bg-grey-500/40",
    dot: "bg-grey-500",
    titleKey: "columns.backlog",
    namespace: "TasksPage",
  },
  todo: {
    bg: "bg-grey-500/40",
    dot: "bg-grey-500",
    titleKey: "columns.todo",
    namespace: "TasksPage",
  },
  in_progress: {
    bg: "bg-orange-500/40",
    dot: "bg-orange-500",
    titleKey: "columns.inProgress",
    namespace: "TasksPage",
  },
  blocked: {
    bg: "bg-red-500/40",
    dot: "bg-red-500",
    titleKey: "columns.blocked",
    namespace: "TasksPage",
  },
  done: {
    bg: "bg-green-500/40",
    dot: "bg-green-500",
    titleKey: "columns.done",
    namespace: "TasksPage",
  },
} as const;

export const PRIORITY_MAP: Record<TaskPriority, StatusMapEntry> = {
  low: { label: "priority.low", color: "info" },
  normal: { label: "priority.normal", color: "gray" },
  high: { label: "priority.high", color: "notice" },
  critical: { label: "priority.critical", color: "negative" },
} as const;

export const PRIORITY_COLOR_CLASS: Record<TaskPriority, string> = {
  low: "content-action-info",
  normal: "content-action-neutral",
  high: "content-action-notice",
  critical: "content-action-negative",
};

export const STATUS_COLOR_CLASS: Record<TaskStatus, string> = {
  backlog: "content-action-neutral",
  todo: "content-action-neutral",
  in_progress: "content-action-notice",
  blocked: "content-action-negative",
  done: "content-action-positive",
};

export const TASK_STATUSES: TaskStatus[] = ["backlog", "todo", "in_progress", "blocked", "done"];

export const TASK_STATUSES_WITHOUT_BACKLOG: TaskStatus[] = ["todo", "in_progress", "blocked", "done"];

export const TASK_TYPE_MAP: Record<TaskType, { label: string; color: BadgeColor }> = {
  kpi: { label: "taskType.kpi", color: "info" },
  first_level_task: { label: "taskType.firstLevelTask", color: "notice" },
  task: { label: "taskType.task", color: "gray" },
} as const;

export const TAG_COLORS: TagColor[] = [
  "gray",
  "info",
  "positive",
  "notice",
  "negative",
  "primary",
  "yellow",
  "indigo",
  "pink",
  "lime",
  "cyan",
];

export const COLUMN_HEADER_STYLES: Record<TaskStatus, { square: string; badge: string }> = {
  backlog: { square: "background-neutral-fill", badge: "content-action-neutral background-neutral-subtle" },
  todo: { square: "background-neutral-fill", badge: "content-action-neutral background-neutral-subtle" },
  in_progress: { square: "background-notice-fill", badge: "content-action-notice background-notice-subtle" },
  blocked: { square: "background-negative-fill", badge: "content-action-negative background-negative-subtle" },
  done: { square: "background-positive-fill", badge: "content-action-positive background-positive-subtle" },
};
